// Local-server registry for the akva-network-scanner page. Servers come from the
// store's configured machines, the same rows tide reads. The topbar machine picker
// selects one; its IP + the fixed akva port becomes the backend URL passed to the
// MFE. Shared between the picker (LocalServerPicker via useLocalServers) and the MFE
// registration (single-spa-config/apps.ts).
//
// This is an external store consumed through useSyncExternalStore: a single
// snapshot is the source of truth; `__OBSERVE_AKVA_BACKEND__` is a read-only
// projection of it the MFE bundle can read across the module boundary, so the
// two can never drift.

import type { ShellStore } from "@observe-tech/marlin-store/contract";
import i18next from "i18next";
import { createNotify } from "@/shell-handle/notify";

// Path prefix of the controller proxy route that fronts the akva-network-scanner
// (marlin/controller/app/src/proxy/proxy.controller.ts). The scanner is no
// longer reached directly on its own host port; all traffic goes through the
// controller's authenticated proxy, which forwards to the scanner over the
// internal Docker bridge. The controller host+port come from the selected
// local server (same endpoint tide uses for the marlin controller).
const AKVA_SCANNER_PROXY_PATH = "/api/proxy/network-scanner";

// Subset of the stored site row (full type: @observe-tech/utils SingleSite).
export type LocalServer = {
  machine_id: number;
  site_id: number;
  name: string;
  ip: string;
  port: string;
};

export type LocalServersSnapshot = {
  servers: LocalServer[];
  selectedMachineId: number | null;
};

// The one window cast for the backend-URL projection, defined once.
function projectBackendGlobal(url: string | undefined): void {
  (
    window as unknown as { __OBSERVE_AKVA_BACKEND__?: string }
  ).__OBSERVE_AKVA_BACKEND__ = url;
}

let cache: Record<number, LocalServer> = {};
let selectedMachineId: number | null = null;
let snapshot: LocalServersSnapshot = { servers: [], selectedMachineId: null };
const subs = new Set<() => void>();

// Recompute the immutable snapshot, refresh the window projection and notify
// subscribers. The snapshot reference only changes here, so useSyncExternalStore
// stays stable between mutations.
function commit(): void {
  snapshot = { servers: Object.values(cache), selectedMachineId };
  projectBackendGlobal(akvaScannerBackendUrl());
  for (const cb of subs) cb();
}

export function trackLocalServers(store: ShellStore): () => void {
  const subscription = store
    .observe(() => store.sites.getAllSites())
    .subscribe({
      next: (sites) => {
        const byMachineId: Record<number, LocalServer> = {};
        for (const server of Object.values(sites))
          byMachineId[server.machine_id] = server;
        cache = byMachineId;
        // Drop a stale selection if the machine is gone from the refreshed list.
        if (selectedMachineId != null && !cache[selectedMachineId])
          selectedMachineId = null;
        commit();
      },
      error: (error: unknown) => {
        console.warn("[shell] local servers query failed", error);
        createNotify()(
          i18next.t("Could not load local servers") ||
            "Could not load local servers",
          "error",
        );
      },
    });
  return () => subscription.unsubscribe();
}

export function getLocalServersSnapshot(): LocalServersSnapshot {
  return snapshot;
}

export function setSelectedMachineId(id: number | null): void {
  selectedMachineId = id;
  commit();
}

export function subscribeLocalServers(cb: () => void): () => void {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
}

// Backend URL for the akva-network-scanner MFE: the selected machine's
// controller (IP + port) plus the proxy path that forwards to the scanner.
// Undefined when nothing is selected or the IP/port is missing.
export function akvaScannerBackendUrl(): string | undefined {
  if (selectedMachineId == null) return undefined;
  const server = cache[selectedMachineId];
  if (!server?.ip || !server?.port) return undefined;
  return `https://${server.ip}:${server.port}${AKVA_SCANNER_PROXY_PATH}`;
}
